/**
 * Toast notification context.
 *
 * Polls for recent unread notifications and displays auto-dismissing
 * pop-ups in the top-right corner of the viewport.
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useRecentNotifications, useMarkAsRead } from '@/hooks/use-notifications';
import type { InAppNotification } from '@/types/notification';

interface Toast {
  id: string;
  notification: InAppNotification;
}

interface ToastContextValue {
  toasts: Toast[];
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue>({ toasts: [], dismiss: () => {} });

export const useToasts = () => useContext(ToastContext);

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'border-l-4 border-l-red-500 bg-red-50',
  high: 'border-l-4 border-l-amber-500 bg-amber-50',
  medium: 'border-l-4 border-l-blue-500 bg-blue-50',
  low: 'border-l-4 border-l-slate-400 bg-slate-50',
};

const SEVERITY_ICONS: Record<string, string> = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'check_circle',
};

const SEVERITY_ICON_COLORS: Record<string, string> = {
  critical: 'text-red-500',
  high: 'text-amber-500',
  medium: 'text-blue-500',
  low: 'text-slate-400',
};

const AUTO_DISMISS_MS = 8_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const shownIds = useRef<Set<string>>(new Set());
  const { data: recent } = useRecentNotifications();
  const { mutate: markRead } = useMarkAsRead();

  // When new recent notifications arrive, show them as toasts
  useEffect(() => {
    if (!recent) return;
    const newToasts: Toast[] = [];
    for (const n of recent) {
      if (!shownIds.current.has(n.id)) {
        shownIds.current.add(n.id);
        newToasts.push({ id: n.id, notification: n });
      }
    }
    if (newToasts.length > 0) {
      setToasts((prev) => [...newToasts, ...prev].slice(0, 5));
    }
  }, [recent]);

  // Auto-dismiss after timeout
  useEffect(() => {
    if (toasts.length === 0) return;
    const timer = setTimeout(() => {
      setToasts((prev) => prev.slice(0, -1));
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [toasts]);

  const dismiss = useCallback(
    (id: string) => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      markRead(id);
    },
    [markRead],
  );

  return (
    <ToastContext.Provider value={{ toasts, dismiss }}>
      {children}

      {/* Toast container — fixed top-right */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 w-96 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto rounded-lg shadow-lg p-4 animate-slide-in ${SEVERITY_STYLES[t.notification.severity] ?? SEVERITY_STYLES.low}`}
          >
            <div className="flex items-start gap-3">
              <span className={`material-symbols-outlined mt-0.5 ${SEVERITY_ICON_COLORS[t.notification.severity]}`}>
                {SEVERITY_ICONS[t.notification.severity]}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{t.notification.title}</p>
                <p className="text-xs text-slate-600 mt-1 line-clamp-2">{t.notification.message}</p>
              </div>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Dismiss notification"
              >
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
